import { Building2, MapPin, CreditCard, Package, ShieldCheck, CheckCircle, LogOut, ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { supabase } from '../../lib/supabase';
import { useOnboardingStore, OnboardingSection } from '../../store/onboarding.store';
import { CompanyWizard } from './Company/CompanyWizard';
import { LocationWizard } from './Locations/LocationWizard';
import { SubscriptionsAndDiscountsWizard } from './Subscriptions/SubscriptionsAndDiscountsWizard';
import { WaiverSetupWizard } from './Waiver/WaiverSetupWizard';
import type { User } from '@supabase/supabase-js';

const SECTIONS = [
  { id: 'company' as OnboardingSection, label: 'Company Setup', icon: Building2 },
  { id: 'locations' as OnboardingSection, label: 'Add Locations', icon: MapPin },
  { id: 'billing' as OnboardingSection, label: 'Billing Setup', icon: CreditCard },
  { id: 'subscriptions' as OnboardingSection, label: 'Subscriptions', icon: Package },
  { id: 'waiver' as OnboardingSection, label: 'Waiver', icon: ShieldCheck },
  { id: 'complete' as OnboardingSection, label: 'Complete', icon: CheckCircle },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { currentSection, setCurrentSection, companyId, companyData, resetOnboarding } = useOnboardingStore();
  const [loading, setLoading] = useState(true);
  const [_user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/owner-signup');
        return;
      }
      setUser(session.user);

      // Get owner role ID
      const { data: ownerRole } = await supabase
        .from('system_roles')
        .select('system_role_id')
        .eq('role', 'owner')
        .single();

      // Check if user already has a company as owner
      const { data: relationships } = await supabase
        .from('user_company_relationships')
        .select('company_id, companies(id, name, setup_complete)')
        .eq('user_id', session.user.id)
        .eq('role_id', ownerRole?.system_role_id || '');

      if (relationships && relationships.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ownerRelation = relationships[0] as any;
        if (ownerRelation.companies?.setup_complete) {
          // Company setup complete - clear stale onboarding state before redirecting
          // to prevent Dashboard's hasIncompleteOnboarding() check from looping back here
          resetOnboarding();
          navigate('/dashboard');
          return;
        }
        // Incomplete setup - sync companyId and load company data
        const existingCompanyId = ownerRelation.company_id;
        if (existingCompanyId) {
          const { setCompanyId } = useOnboardingStore.getState();
          setCompanyId(existingCompanyId);
        }
      } else {
        // No relationships found - check if user created a company that's orphaned
        // This can happen if the atomic RPC function partially failed
        const { data: orphanedCompany } = await supabase
          .from('companies')
          .select('id, name, setup_complete')
          .eq('created_by', session.user.id)
          .eq('setup_complete', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (orphanedCompany) {
          // Fix the orphaned company by creating the missing relationship
          const { data: ownerRole } = await supabase
            .from('system_roles')
            .select('system_role_id')
            .eq('role', 'owner')
            .single();

          if (ownerRole) {
            const { error: relError } = await supabase
              .from('user_company_relationships')
              .insert({
                user_id: session.user.id,
                company_id: orphanedCompany.id,
                role_id: ownerRole.system_role_id,
                onboarding_complete: false,
              });

            if (!relError) {
              // Update last_selected_company
              await supabase
                .from('user_profiles')
                .update({ last_selected_company: orphanedCompany.id })
                .eq('id', session.user.id);

              // Refresh session to get updated JWT
              await supabase.auth.refreshSession();

              const { setCompanyId } = useOnboardingStore.getState();
              setCompanyId(orphanedCompany.id);
            }
          }
        }
      }

      // Load company data if companyId exists (either from relationship or fixed orphan)
      const finalCompanyId = companyId || useOnboardingStore.getState().companyId;
      if (finalCompanyId) {
        const { setCompanyData } = useOnboardingStore.getState();
        
        // Load existing company data from database to populate form fields
        // This ensures data persists across browsers/windows
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('name, slogan, ein, contact_fn, contact_ln, company_email, phone, address, address2, city, state, zip, geofence_lat, geofence_lng, logo_url')
          .eq('id', finalCompanyId)
          .single();
        
        if (companyError) {
          // Continue without loading - user can still fill out form
        } else if (companyData) {
          // Sanitize DB values: filter out literal "null"/"NULL" strings
          // that can appear in nullable text columns (issue #563)
          const clean = (val: string | null | undefined): string =>
            !val || val.toLowerCase() === 'null' ? '' : val;

          // Populate onboarding store with existing data
          setCompanyData({
            name: clean(companyData.name),
            slogan: clean(companyData.slogan),
            ein: clean(companyData.ein),
            contactFirstName: clean(companyData.contact_fn),
            contactLastName: clean(companyData.contact_ln),
            email: clean(companyData.company_email),
            phone: clean(companyData.phone),
            street: clean(companyData.address),
            street2: clean(companyData.address2),
            city: clean(companyData.city),
            state: clean(companyData.state),
            zip: clean(companyData.zip),
            latitude: companyData.geofence_lat || undefined,
            longitude: companyData.geofence_lng || undefined,
            place_id: undefined, // place_id is stored on locations, not companies
            logoUrl: clean(companyData.logo_url),
          });
        }
      }
      // Resume onboarding from current section (stored in localStorage)
      // The component will render the correct section based on currentSection

      setLoading(false);
    };

    checkAuth();
  }, [navigate, companyId]);

  const handleSignOut = async () => {
    resetOnboarding();
    await supabase.auth.signOut();
    navigate('/');
  };

  const getCurrentSectionIndex = () => {
    return SECTIONS.findIndex(s => s.id === currentSection);
  };

  const handleCompanyComplete = () => {
    setCurrentSection('locations');
  };

  const handleLocationsComplete = () => {
    // Allow users to skip billing and go directly to subscriptions
    // Billing can be set up later from the dashboard
    // Track that billing was skipped so back button navigation is correct
    setCurrentSection('subscriptions');
  };


  const handleSubscriptionsComplete = () => {
    setCurrentSection('waiver');
  };

  const handleWaiverComplete = () => {
    setCurrentSection('complete');
  };

  const handleOnboardingComplete = async () => {
    // Mark company setup as complete
    if (companyId) {
      await supabase
        .from('companies')
        .update({ setup_complete: true })
        .eq('id', companyId);
    }
    resetOnboarding();
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111010] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  const renderSection = () => {
    switch (currentSection) {
      case 'company':
        return <CompanyWizard onComplete={handleCompanyComplete} />;
      
      case 'locations':
        return companyId ? (
          <LocationWizard
            companyId={companyId}
            companyData={companyData}
            onComplete={handleLocationsComplete}
            onBack={() => setCurrentSection('company')}
          />
        ) : (
          <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
                <MapPin size={32} className="text-rollops-accent" />
              </div>
              <h2 className="text-2xl font-bold text-[#EBFFEE]">Add Locations</h2>
              <p className="text-[#A9A3A3] mt-2">Please complete company setup first</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentSection('company')}
                className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
              >
                Back to Company Setup
              </button>
            </div>
          </div>
        );
      
      case 'billing':
        return (
          <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
                <CreditCard size={32} className="text-rollops-accent" />
              </div>
              <h2 className="text-2xl font-bold text-[#EBFFEE]">Billing Setup</h2>
              <p className="text-[#A9A3A3] mt-2">Configure how you&apos;ll accept payments</p>
            </div>
            <p className="text-[#A9A3A3] text-center mb-6">
              Set up your payment processing with Stripe to start accepting payments from your members. You can also set this up later from your dashboard.
            </p>
            <div className="flex flex-col gap-3">
              <div className="flex gap-4">
                <button
                  onClick={() => setCurrentSection('locations')}
                  className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
                >
                  Back
                </button>
                <Link
                  to="/onboarding/billing"
                  className="flex-1 py-3 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors text-center"
                >
                  Set Up Billing
                </Link>
              </div>
              <button
                onClick={() => setCurrentSection('subscriptions')}
                className="w-full py-3 px-4 bg-transparent hover:bg-[#363232] text-[#A9A3A3] font-semibold rounded-lg transition-colors border border-[#504A4A]"
              >
                Skip for now
              </button>
            </div>
          </div>
        );
      
      case 'subscriptions':
        return companyId ? (
          <SubscriptionsAndDiscountsWizard
            companyId={companyId}
            onComplete={handleSubscriptionsComplete}
            onBack={() => {
              // If user visited billing section, go back to billing
              // Otherwise, go back to locations (since billing was skipped)
              const { billingSectionVisited } = useOnboardingStore.getState();
              setCurrentSection(billingSectionVisited ? 'billing' : 'locations');
            }}
          />
        ) : (
          <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
                <Package size={32} className="text-rollops-accent" />
              </div>
              <h2 className="text-2xl font-bold text-[#EBFFEE]">Subscriptions & Discounts</h2>
              <p className="text-[#A9A3A3] mt-2">Please complete company setup first</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentSection('company')}
                className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
              >
                Back to Company Setup
              </button>
            </div>
          </div>
        );
      
      case 'waiver':
        return companyId ? (
          <WaiverSetupWizard
            companyId={companyId}
            onComplete={handleWaiverComplete}
            onBack={() => setCurrentSection('subscriptions')}
            onSkip={handleWaiverComplete}
          />
        ) : (
          <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={32} className="text-rollops-accent" />
              </div>
              <h2 className="text-2xl font-bold text-[#EBFFEE]">Liability Waiver</h2>
              <p className="text-[#A9A3A3] mt-2">Please complete company setup first</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentSection('company')}
                className="flex-1 py-3 px-4 bg-[#504A4A] hover:bg-[#5A5454] text-white font-semibold rounded-lg transition-colors"
              >
                Back to Company Setup
              </button>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-8 border border-[#504A4A]">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-green-600/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={48} className="text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#EBFFEE]">You&apos;re All Set!</h2>
              <p className="text-[#A9A3A3] mt-2">Your gym is ready to go</p>
            </div>
            
            <div className="bg-[#363232] rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-[#EBFFEE] mb-2">What&apos;s next?</h3>
              <ul className="space-y-2 text-sm text-[#A9A3A3]">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  Invite your staff members
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  Add your class schedule
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  Start enrolling members
                </li>
              </ul>
            </div>

            <button
              onClick={handleOnboardingComplete}
              className="w-full py-4 px-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold rounded-lg transition-colors text-lg"
            >
              Go to Dashboard
            </button>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#111010] font-['Inter']">
      <Helmet>
        <title>Gym Setup - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>
      
      {/* Header */}
      <header className="border-b border-[#504A4A] bg-[#111010]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={20} className="text-[#A9A3A3]" />
            <img src="/rollops-logo.png" alt="RollOps" className="w-10 h-10" />
            <span className="text-2xl font-bold text-[#EBFFEE]">
              Roll<span className="text-rollops-accent">O</span>ps
            </span>
          </Link>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </header>

      {/* Progress Tabs */}
      <div className="border-b border-[#504A4A] bg-[#111010]">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center gap-2 overflow-x-auto">
            {SECTIONS.map((section, index) => {
              const currentIndex = getCurrentSectionIndex();
              const isActive = section.id === currentSection;
              const isCompleted = index < currentIndex;
              const isAccessible = index <= currentIndex;
              const Icon = section.icon;

              return (
                <React.Fragment key={section.id}>
                  <button
                    onClick={() => isAccessible && setCurrentSection(section.id)}
                    disabled={!isAccessible}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'bg-[#9B1421] text-white'
                        : isCompleted
                        ? 'bg-green-600/20 text-green-500 hover:bg-green-600/30'
                        : 'bg-[#363232] text-[#A9A3A3]'
                    } ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                  >
                    <Icon size={16} />
                    <span className="hidden sm:inline">{section.label}</span>
                  </button>
                  {index < SECTIONS.length - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        isCompleted ? 'bg-green-600' : 'bg-[#504A4A]'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {renderSection()}
      </main>
    </div>
  );
}

